//Employee engagement

import React, { useState, useEffect } from "react";
import "./index.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { getClientUsers, getRepeatUsers } from "../../actions/dataLoad";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { useUserTokenSelector } from "store/reducers";
import { useLocation } from "react-router-dom";
import { GrDownload } from "react-icons/gr";
import { CiCircleInfo } from "react-icons/ci";
import { getTeamLocationForFilter } from "actions/assessment";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { startLoading, stopLoading } from "store/actions";
import { useDispatch } from "react-redux";
import { Template } from "Containers/Overview/template";

const COLORS = ["#7E8ED9", "#49AF7C", "#89D4AF"];

export const Overview = ({ history, adminCorporateId }) => {
  const userToken = useUserTokenSelector();
  const [uniqueUsers, setUniqueUsers] = useState("");
  const [genderChartData, setGenderChartData] = useState([]);
  const [signedUpUsers, setSignedUpUsers] = useState("");
  const [monthlyLoginData, setMonthlyLoginData] = useState([]);
  const [currMonthSessionCount, setCurrMonthSessionCount] = useState("");
  const [sessionCountChange, setSessionCountChange] = useState("");
  const [uniqueUserChange, setUniqueUserChange] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [selectTimeRange, setSelectTimeRange] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedGender, setSelectedGender] = useState("Overall");
  const [yAxisTicks, setYAxisTicks] = useState([0, 20, 40, 60, 80, 100]); // State for Y-axis ticks
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState("All Time");
  const yearsArray = ["All Time", "2023", "2024"];
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const timeRangeArray = [
    "Today",
    "This Week",
    "This Month",
    "Last 3 months",
    "Last 6 months",
    "Last 1 year",
    "All Time",
    "Custom",
  ];
  const [teamArray, setTeamArray] = useState(["All Teams"]); // Dynamic team options
  const [locationArray, setLocationArray] = useState(["All Locations"]); // Dynamic location options
  const [groupedTeamLocations, setGroupedTeamLocations] = useState({}); // Mapping of team to locations
  const showError = (text) => {
    setError(text);
    setOpen(true);
  };
  const [showReport, setShowReport] = useState(false);
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({
    signups: {
      total: 0,
      previous: 0,
    },
    active: {
      total: 0,
      previous: 0,
    },
    logins: {
      total: 0,
      previous: 0,
    },
  });

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      setOpen(false);
    }
    setOpen(false);
  };

  const timeRangeMap = new Map([
    ["Today", "today"],
    ["This Week", "7d"],
    ["This Month", "30d"],
    ["Last 3 months", "3m"],
    ["Last 6 months", "6m"],
    ["Last 1 year", "1y"],
    ["All Time", "all"],
    ["Custom", "custom"],
  ]);

  function mapTimeRangeToApiValue(timeRange) {
    return timeRangeMap.get(timeRange) || "all";
  }

  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setSelectTimeRange(value);
    if (value === "custom") {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const handleCustomDateChange = (date) => {
    if (Array.isArray(date)) {
      const [startDate, endDate] = date;
      setCustomDateRange([startDate, endDate]);
      if (startDate && endDate) {
        setShowCustomCalendar(false);
      }
      return;
    }
    setCustomDateRange(date);
  };

  const getCustomDates = () => {
    if (Array.isArray(customDateRange)) {
      const [startDate, endDate] = customDateRange;
      return { startDate, endDate, singleDate: startDate };
    }
    return { startDate: null, endDate: null, singleDate: customDateRange };
  };

  // Helper function to calculate Y-axis ticks dynamically
  const calculateYAxisTicks = (data, dataKey = "value") => {
    const defaultTicks = [0, 20, 40, 60, 80, 100];
    if (!data || !Array.isArray(data) || data.length === 0) {
      return defaultTicks;
    }

    const values = data
      .map((item) => item[dataKey])
      .filter((v) => typeof v === "number" && !isNaN(v));
    if (values.length === 0) {
      return defaultTicks;
    }

    const maxDataValue = Math.max(0, ...values);

    if (maxDataValue === 0) {
      return [0, 10, 20, 30, 40, 50]; // Or simply [0]
    }

    let tickInterval;
    // Determine a 'nice' interval based on the max value
    if (maxDataValue <= 10) tickInterval = 1;
    else if (maxDataValue <= 25) tickInterval = 5;
    else if (maxDataValue <= 50) tickInterval = 10;
    else if (maxDataValue <= 100) tickInterval = 20;
    else if (maxDataValue <= 250) tickInterval = 50;
    else if (maxDataValue <= 500) tickInterval = 100;
    else {
      const orderOfMagnitude = Math.pow(
        10,
        Math.floor(Math.log10(maxDataValue))
      );
      tickInterval = orderOfMagnitude / 2; // Adjust for larger scales
      if (maxDataValue / tickInterval > 7) {
        // Aim for 5-7 ticks
        tickInterval = orderOfMagnitude;
      }
    }

    tickInterval = Math.max(1, Math.round(tickInterval)); // Ensure interval is at least 1

    const niceMaxValue = Math.ceil(maxDataValue / tickInterval) * tickInterval;

    const ticks = [];
    for (let i = 0; i <= niceMaxValue; i += tickInterval) {
      ticks.push(i);
    }

    return ticks.length > 1
      ? [...new Set([0, ...ticks])]
      : maxDataValue > 0
      ? [0, maxDataValue]
      : [0];
  };

  const fetchTeamAndLocationFilters = async () => {
    if (!userToken) return;
    try {
      const res = await getTeamLocationForFilter(userToken);
      if (res && res.data && Array.isArray(res.data.teamLocations)) {
        const fetchedTeams = new Set();
        const fetchedLocations = new Set();
        const groupedData = {};

        res.data.teamLocations.forEach((item) => {
          if (item.teamName) fetchedTeams.add(item.teamName);
          if (item.location) fetchedLocations.add(item.location);
          if (item.teamName) {
            if (!groupedData[item.teamName]) {
              groupedData[item.teamName] = [];
            }
            if (item.location && !groupedData[item.teamName].includes(item.location)) {
              groupedData[item.teamName].push(item.location);
            }
          }
        });

        setTeamArray(["All Teams", ...Array.from(fetchedTeams).sort()]);
        setLocationArray(["All Locations", ...Array.from(fetchedLocations).sort()]); // Initial full list
        setGroupedTeamLocations(groupedData);
      }
    } catch (error) {
      console.error("Error fetching team/location filters:", error);
    }
  };

  useEffect(() => {
    fetchTeamAndLocationFilters();
  }, []);

  useEffect(() => {
    const { startDate, endDate } = getCustomDates();
    const isCustomDateValid =
      selectTimeRange !== "custom" || (startDate && endDate);

    if (!isCustomDateValid) return;
    getAllClientUsers();
    getAllRepeatUsers();
  }, [selectedTeam, selectedLocation, selectTimeRange, customDateRange]);

  const getAllClientUsers = async () => {
    setLoading(true);
    try {
      let team = "",
        location = "";
      if (
        selectedTeam !== "all" &&
        selectedLocation !== "all"
      ) {
        team = selectedTeam;
        location = selectedLocation;
      }
      const res = await getClientUsers(
        userToken,
        adminCorporateId,
        team,
        location,
        selectTimeRange,
        customDateRange
      );
      setSignedUpUsers(res.data.signedUpUsers);
      setGenderChartData(res.data.genderChartData);
    } catch (err) {
      if (err.response.data.redirectToSignIn) {
        showError(err.response.data.error);
        setTimeout(
          () =>
            history.push({
              pathname: "/signin",
              state: { referer: location.pathname },
            }),
          3000
        );
      } else {
        showError(err.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getAllRepeatUsers = async () => {
    setLoading(true);
    try {

      const res = await getRepeatUsers(
        userToken,
        adminCorporateId,
        selectedTeam,
        selectedLocation,
        selectTimeRange,
        customDateRange
      );
      const rawData = res.data.data; // This is the 'response.data' object from the backend
      let mappedData;

      // Standardize the key for the bar value to 'value'
      // and the key for the x-axis label to 'label'
      if (selectTimeRange === "today" || selectTimeRange === "custom") {
        // For 'today' or 'custom', rawData is { date: 'YYYY-MM-DD', totalLogins: X }
        mappedData = [{ label: rawData.date, value: rawData.totalLogins }];
      } else if (selectTimeRange === "7d") {
        // For '7d', rawData is an array of { day: 'Monday', count: X }
        mappedData = rawData.map((item) => ({
          label: item.day,
          value: item.count,
        }));
      } else if (selectTimeRange === "30d") {
        // For '30d', rawData is an array of { week: 'Week 1', count: X }
        mappedData = rawData.map((item) => ({
          label: item.week,
          value: item.count,
        }));
      } else {
        // For '3m', '6m', 'all'. rawData is an array of { month/year: ..., count: ... }
        mappedData = rawData.map((item) => ({
          label: item.month || item.year?.toString(), // Use month if available, else year
          value: item.count,
        }));
      }
      setMonthlyLoginData(mappedData);
      setYAxisTicks(calculateYAxisTicks(mappedData, "value")); // Calculate and set Y-axis ticks
      setUniqueUsers(res.data.uniqueUsers);
      setCurrMonthSessionCount(res.data.currMonthSessionCount);
      setSessionCountChange(res.data.sessionCountChange);
      setUniqueUserChange(res.data.uniqueUserChange);
    } catch (err) {
      if (err.response.data.redirectToSignIn) {
        showError(err.response.data.error);
        setTimeout(
          () =>
            history.push({
              pathname: "/signin",
              state: { referer: location.pathname },
            }),
          3000
        );
      } else {
        showError(err.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#394B42"
        fontSize={14}
        fontFamily="Quicksand"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {(percent * 100).toFixed(0)}%
      </text>
    );
  };

  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {payload.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 4,
              fontFamily: "Quicksand",
              fontSize: "14px",
              color: "#394B42",
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            {entry.value}
          </li>
        ))}
      </ul>
    );
  };

    // Handler for Team dropdown change
    const handleTeamChange = (e) => {
      const newTeam = e.target.value;
      setSelectedTeam(newTeam);
  
      // Update location options based on the selected team
      if (newTeam === "All Teams") {
        // If "All Teams" is selected, show all unique locations
        const allUniqueLocations = [
          "All Locations",
          ...new Set(Object.values(groupedTeamLocations).flat()),
        ].sort();
        setLocationArray(allUniqueLocations);
        setSelectedLocation("All Locations"); // Reset selected location
      } else if (groupedTeamLocations[newTeam]) {
        // If a specific team is selected, show its locations
        const teamSpecificLocations = groupedTeamLocations[newTeam].sort();
        setLocationArray(["All Locations", ...teamSpecificLocations]);
        // Set selected location to the first available location for that team, or "All Locations"
        setSelectedLocation(teamSpecificLocations.length > 0 ? teamSpecificLocations[0] : "All Locations");
      } else {
        // Fallback if no locations found for the team
        setLocationArray(["All Locations"]);
        setSelectedLocation("All Locations");
      }
    };
  
    // Handler for Location dropdown change
    const handleLocationChange = (e) => {
      if(selectedTeam === "all" || !selectedTeam) {
        setOpen(true);
        setError("Please select team first!")
        return;
      }
      const newLocation = e.target.value;
      setSelectedLocation(newLocation);
  
      // If "All Teams" is currently selected, try to infer the specific team
      if (selectedTeam === "All Teams" && newLocation !== "All Locations") {
        let inferredTeam = "All Teams"; // Default if not found
        for (const teamName in groupedTeamLocations) {
          if (groupedTeamLocations[teamName].includes(newLocation)) {
            inferredTeam = teamName;
            break; // Found the first matching team
          }
        }
        setSelectedTeam(inferredTeam);
      } else if (newLocation === "All Locations") {
        // If location is reset to "All Locations", ensure team is "All Teams"
        setSelectedTeam("All Teams");
      }
      // If selectedTeam is already specific, changing location within that team's options
      // doesn't require changing selectedTeam, as the team filter is already applied.
    };

  const empEngagementData = () => {
    const dataJson = {
      signups: userInfo.signups,
      active: userInfo.active,
      logins: userInfo.logins,
      uniqueUsers: uniqueUsers,
      genderChartData: genderChartData,
      signedUpUsers: signedUpUsers,
      monthlyLoginData: monthlyLoginData,
      currMonthSessionCount: currMonthSessionCount,
      sessionCountChange: sessionCountChange,
      uniqueUserChange: uniqueUserChange,
      overviewItems: [],
      contentTrends: [],
      engagement: [],
      distribution: [],
      engagedEmp: [],
    };
    return dataJson;
  };

    const handleShowReport = () => {
      setIsDownloading(true);
      setShowReport(true);
      dispatch(startLoading());
    };

    const handleReportClose = () => {
      setIsDownloading(false);
      setShowReport(false);
      dispatch(stopLoading());
    };

  return (
    <div className="user-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      {showCustomCalendar && (
        <div
          className="Users-overlay-background"
          onClick={() => setShowCustomCalendar(false)}
          aria-label="Close calendar"
        ></div>
      )}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>

      <div className="users-top">
        <div className="users-filter">
          <select
            value={selectedLocation}
            onChange={handleLocationChange}
          >
            {locationArray.map((location) => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>
        <div className="users-filter">
          <select
            value={selectedTeam}
            onChange={handleTeamChange}
          >
            {teamArray.map((team) => (
              <option key={team} value={team}>{team}</option>
            ))}
          </select>
        </div>
        <div className="users-filter">
          <select
            id="timeRangeFilter"
            data-testid="timeRangeFilter"
            value={selectTimeRange}
            aria-label="Time Range"
            // onChange={(e) => setSelectTimeRange(e.target.value)}
            onChange={handleTimeRangeChange}
          >
            {timeRangeArray.map((time) => (
              <option key={time} value={mapTimeRangeToApiValue(time)}>
                {time}
              </option>
            ))}
          </select>
        </div>
        <div className={`users-download-div ${isDownloading ? "disabled" : ""}`} onClick={!isDownloading ? handleShowReport : null}>
          <GrDownload />
          <span>Download</span>
        </div>
      </div>

      <div className="user-logins-chart">
        {showCustomCalendar && (
          <div className="custom-calendar-container">
            <Calendar
              onChange={handleCustomDateChange}
              value={customDateRange}
              selectRange
            />
          </div>
        )}
        <div className="user-cards-header">
          <p className="user-logins-chart-heading">User Logins</p>
          <CiCircleInfo className="user-infoIcon" />
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={monthlyLoginData}
            barSize={45}
            margin={{ top: 5, right: 5, bottom: 5, left: -25 }} // Adjusted top margin slightly
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label" // Use 'label' for X-axis data
              // tick={{
              //   fill: "#394B42",
              //   fontSize: "12px",
              //   fontFamily: "Quicksand",
              //   fontWeight: 500,
              // }}
              // axisLine={{ stroke: "#394B42" }}
              tickLine={false}
            />
            <YAxis
              tick={{
                fill: "#394B42",
                fontSize: "12px",
                fontFamily: "Quicksand",
                fontWeight: 500,
              }}
              ticks={yAxisTicks} // Use dynamic ticks
              axisLine={{ stroke: "#394B42" }}
              tickLine={false}
            />
            <Tooltip />
            <Bar dataKey="value" fill="#49AF7C" radius={[4, 4, 0, 0]} />{" "}
            {/* Use 'value' for Bar dataKey */}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="user-stats">
        <div className="signed-up-users">
          <div className="user-cards-header">
            <p className="user-logins-chart-heading">Signed up users</p>
            <CiCircleInfo className="user-infoIcon" />
          </div>
          <div className="user-signup-center">
            <p className="user-signup-data">{signedUpUsers}</p>
          </div>
        </div>

        <div className="gender-chart">
          <div className="user-cards-header">
            <p className="user-logins-chart-heading">Gender Ratio</p>
            <CiCircleInfo className="user-infoIcon" />
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={genderChartData}
                dataKey="value"
                nameKey="gender"
                cx="40%"
                cy="50%"
                outerRadius={80}
                // label={({ percent, value }) =>
                //   `${(percent * 100).toFixed(0)}%`
                // }
                label={renderCustomLabel}
                labelLine={false}
              >
                {genderChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                content={renderLegend}
                layout="vertical"
                align="right"
                verticalAlign="middle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {showReport && <Template data={empEngagementData()} close={handleReportClose} />}
    </div>
  );
};

export default Overview;
